import { defineStore } from 'pinia'
import { keyBy, maxBy } from 'lodash'
import moment from 'moment'
import camelCase from 'camelcase'
import PresentRepository, { PresentParams } from '@/repositories/present-repository'
import { Present, PresentAlertLevel, PresentDiff } from '@/models/Present'
import { StationListItem } from '@/models/InitData'
import repositoryFactory from '@/factories/repository-factory'

const storeKey = 'present'

type Store = {
  present: {
    [key in string]: Present
  }
  loading: boolean
}

export const usePresentStore = defineStore(storeKey, {
  state: () =>
    ({
      present: {},
      loading: true,
    } as Store),

  getters: {
    getValue:
      state =>
      <T = number>(stationId: string, _elementId: string) => {
        const elementId = camelCase(_elementId) as keyof Present

        if (!state.present || !state.present[stationId]) {
          return
        }

        return state.present[stationId][elementId] as T
      },

    getDiff: state => (stationId: string, _elementId: string) => {
      const elementId = camelCase(_elementId) as keyof PresentDiff

      if (state.present === undefined || state.present[stationId] === undefined) {
        return
      }

      return state.present[stationId].diff[elementId] as number
    },

    getAlertLevel: state => (stationId: string, _elementId: string) => {
      const elementId = camelCase(_elementId) as keyof PresentAlertLevel

      if (!state.present || !state.present[stationId]) {
        return
      }

      return state.present[stationId as string].alertLevel[elementId]
    },

    getTime: state => (stationId: string, _format?: string) => {
      if (!state.present || !state.present[stationId]) {
        return '--:--'
      }

      const format = _format || 'YYYY年MM月DD日 HH:mm'

      return moment(state.present[stationId].time).format(format)
    },

    recentTime: state => {
      return maxBy(Object.values(state.present), d => {
        return moment(d.time).unix()
      })
    },
  },

  actions: {
    async fetch(params: PresentParams) {
      const repository = (await repositoryFactory.create()).get(storeKey) as PresentRepository
      const res = await repository.get(params)

      this.present = keyBy(res.items, 'stationId')
    },

    fetchAll(stationList: StationListItem[]) {
      const stationListParams = stationList.map(station => ({
        stationId: station.stationId,
        target: [...station.fieldId, 'rain_day', 'rain_min_10', 'rain_min_60'],
      }))

      if (!stationList || stationList.length === 0) {
        return
      }

      this.fetch({ stationList: stationListParams }).then(() => {
        this.loading = false
      })
    },

    findByStationId(stationId?: string) {
      if (!stationId) {
        return {} as Present
      }

      return this.present[stationId]
    },
  },
})
